/**
 * One composer for every outbound message: the question raised during triage,
 * the decision summary sent with an approval, and the note that goes with a
 * rejection. Same block, same destination picker, same "read it before it
 * sends" contract.
 *
 * "Copy link" copies the PING, never the body. The message itself always stays
 * in the app thread (handover §7), so a stock figure can never end up pasted
 * into a channel.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const COPIED_LABEL_MS = 1600;

interface StocktakeComposerProps {
  title: string;
  hint: string;
  cta: string;
  value: string;
  onChange: (value: string) => void;
  onCancel?: () => void;
  onSend: () => void;
  /** Text pasted by "Copy link" — the ping, not the body. */
  pingText: string;
  /**
   * How the other side gets pinged. `null` when the caller may not read the
   * notify config (Warehouse Staff and below): the sentence is dropped rather
   * than asserting a default the caller cannot verify.
   */
  notifyLabel: string | null;
  /** Omitted when the caller may not change the notify config. */
  onChangeNotify?: (() => void) | undefined;
  isSending?: boolean;
  /**
   * The composer is also rendered inside a Dialog that already prints the
   * title in its header. Suppress the heading there rather than shipping it
   * twice.
   */
  hideTitle?: boolean;
}

export function StocktakeComposer({
  title,
  hint,
  cta,
  value,
  onChange,
  onCancel,
  onSend,
  pingText,
  notifyLabel,
  onChangeNotify,
  isSending = false,
  hideTitle = false,
}: StocktakeComposerProps) {
  const { t } = useTranslation('inventory');
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const copyLink = () => {
    void navigator.clipboard?.writeText(pingText);
    setCopied(true);
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), COPIED_LABEL_MS);
  };

  return (
    <div className={cn('rounded-lg border bg-muted/40 p-3', hideTitle ? 'mt-0' : 'mt-3')}>
      {hideTitle ? null : <h4 className="text-[12.5px] font-semibold">{title}</h4>}
      <p className={cn('text-[11.5px] text-muted-foreground', hideTitle ? '' : 'mt-0.5')}>
        {hint}
        {notifyLabel ? (
          <>
            {' '}
            {t('stocktakes.composer.ping')}{' '}
            <b className="font-semibold text-foreground">{notifyLabel}</b>
            {onChangeNotify ? (
              <>
                {' '}
                <button
                  type="button"
                  onClick={onChangeNotify}
                  className="rounded-sm font-semibold text-threadheads-purple underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('stocktakes.composer.change')}
                </button>
              </>
            ) : null}
          </>
        ) : null}
      </p>
      {/* Proportional, not monospace: the figures are bullet lines now, so the
          draft reads here the way it will read in the other side's thread.
          Monospace only made sense while the body carried padded columns. */}
      <Textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-label={title}
        className="mt-2 min-h-[150px] resize-y bg-background text-xs leading-relaxed"
      />
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {onCancel ? (
          <Button variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={onCancel}>
            {t('stocktakes.composer.cancel')}
          </Button>
        ) : null}
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs"
          onClick={copyLink}
          aria-live="polite"
        >
          {copied ? t('stocktakes.composer.linkCopied') : t('stocktakes.composer.copyLink')}
        </Button>
        <Button
          size="sm"
          className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
          onClick={onSend}
          disabled={isSending}
        >
          {isSending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : null}
          {cta}
        </Button>
      </div>
    </div>
  );
}
